import { useCallback, useSyncExternalStore } from "react";
import { deviceSettingsStorage, getDeviceSetting, setDeviceSetting } from "#device-settings-store";
import type { DeviceSettingKey, DeviceSettings } from "#device-settings-schemas";

const DEVICE_PREFIX = "device:";

export function useDeviceSetting<K extends DeviceSettingKey>(
  key: K,
): [DeviceSettings[K], (value: DeviceSettings[K]) => void] {
  const subscribe = useCallback(
    (onChange: () => void) => {
      // prefixStorage does not customize `watch`, so the callback receives the
      // full root key (e.g. "device:theme"). Match against the prefixed form.
      const fullKey = DEVICE_PREFIX + key;
      const unwatch = deviceSettingsStorage.watch((_event, changedKey) => {
        if (changedKey === fullKey) {
          onChange();
        }
      });
      return () => {
        unwatch();
      };
    },
    [key],
  );
  const getSnapshot = useCallback(() => getDeviceSetting(key), [key]);
  const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const setValue = useCallback((next: DeviceSettings[K]) => setDeviceSetting(key, next), [key]);
  return [value, setValue];
}
